import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Heart, 
  Mail, 
  Check, 
  X, 
  Clock, 
  MessageCircle,
  MapPin,
  User,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface AdoptionRequest {
  id: string;
  requester_id: string;
  message: string;
  meeting_availability: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  adoption_post: {
    id: string;
    pet_name: string;
    species: string;
    breed?: string;
    image_url?: string;
    location: string;
    title: string;
  };
  requester_profile: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    bio: string | null;
  };
}

export default function AdoptionRequests() {
  const { user, loading: authLoading } = useAuth();
  const [requests, setRequests] = useState<AdoptionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<AdoptionRequest | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  // Redirect to auth if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/auth" replace />;
  }

  useEffect(() => {
    if (user) {
      fetchAdoptionRequests();
    }
  }, [user]);

  const fetchAdoptionRequests = async () => {
    if (!user) return;

    try {
      // First get user's adoption posts
      const { data: userPosts, error: userPostsError } = await supabase
        .from('adoption_posts')
        .select('id')
        .eq('user_id', user.id);

      if (userPostsError) throw userPostsError;

      const postIds = userPosts?.map(p => p.id) || [];
      
      if (postIds.length === 0) {
        setRequests([]);
        setLoading(false);
        return;
      }

      // Get adoption requests for those posts with separate queries
      const { data: requestsData, error } = await supabase
        .from('adoption_requests')
        .select('*')
        .in('adoption_post_id', postIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!requestsData || requestsData.length === 0) {
        setRequests([]);
        return;
      }

      // Get adoption posts data
      const { data: postsData, error: postsError } = await supabase
        .from('adoption_posts')
        .select('id, pet_name, species, breed, image_url, location, title')
        .in('id', requestsData.map(r => r.adoption_post_id));

      if (postsError) throw postsError;

      // Get requester profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, username, display_name, avatar_url, bio')
        .in('user_id', requestsData.map(r => r.requester_id));

      if (profilesError) throw profilesError;

      // Transform the data to match our interface
      const transformedRequests = requestsData.map(request => {
        const adoptionPost = postsData?.find(p => p.id === request.adoption_post_id);
        const profile = profilesData?.find(p => p.user_id === request.requester_id);

        return {
          id: request.id,
          requester_id: request.requester_id,
          message: request.message,
          meeting_availability: request.meeting_availability,
          status: request.status as 'pending' | 'approved' | 'rejected',
          created_at: request.created_at,
          updated_at: request.updated_at,
          adoption_post: {
            id: adoptionPost?.id || '',
            pet_name: adoptionPost?.pet_name || '',
            species: adoptionPost?.species || '',
            breed: adoptionPost?.breed,
            image_url: adoptionPost?.image_url,
            location: adoptionPost?.location || '',
            title: adoptionPost?.title || ''
          },
          requester_profile: {
            username: profile?.username || '',
            display_name: profile?.display_name,
            avatar_url: profile?.avatar_url,
            bio: profile?.bio
          }
        };
      });

      setRequests(transformedRequests);
    } catch (error) {
      console.error('Error fetching adoption requests:', error);
      toast.error('Errore nel caricamento delle richieste');
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId: string, status: 'approved' | 'rejected', replyMsg?: string) => {
    try {
      const { error } = await supabase
        .from('adoption_requests')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      // Create notification for requester
      const request = requests.find(r => r.id === requestId);
      if (request && replyMsg) {
        await supabase.rpc('create_notification', {
          p_user_id: request.requester_id,
          p_type: `adoption_${status}`,
          p_title: status === 'approved' 
            ? `Richiesta approvata per ${request.adoption_post.pet_name}!`
            : `Richiesta rifiutata per ${request.adoption_post.pet_name}`,
          p_message: replyMsg,
          p_data: {
            adoption_post_id: request.adoption_post.id,
            adoption_request_id: requestId
          }
        });
      }

      toast.success(
        status === 'approved' 
          ? 'Richiesta approvata!' 
          : 'Richiesta rifiutata'
      );

      fetchAdoptionRequests();
      setReplyModalOpen(false);
      setDetailModalOpen(false);
      setReplyMessage('');
    } catch (error) {
      console.error('Error updating request status:', error);
      toast.error('Errore nell\'aggiornamento dello stato');
    }
  };

  const handleReply = async (action: 'approved' | 'rejected') => {
    if (!selectedRequest) return;

    if (!replyMessage.trim()) {
      toast.error('Scrivi un messaggio di risposta');
      return;
    }

    setReplyLoading(true);
    await updateRequestStatus(selectedRequest.id, action, replyMessage);
    setReplyLoading(false);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: { variant: 'secondary' as const, label: 'In attesa', icon: Clock },
      approved: { variant: 'default' as const, label: 'Approvata', icon: Check },
      rejected: { variant: 'destructive' as const, label: 'Rifiutata', icon: X }
    };

    const config = variants[status as keyof typeof variants];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = {
    pending: requests.filter(r => r.status === 'pending'),
    approved: requests.filter(r => r.status === 'approved'),
    rejected: requests.filter(r => r.status === 'rejected'),
    all: requests
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="glass border-white/20">
              <CardContent className="p-6">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-muted rounded w-1/3" />
                  <div className="h-3 bg-muted rounded w-2/3" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 pb-20 lg:pb-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <Heart className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-white">Richieste di Adozione</h1>
            <p className="text-muted-foreground">
              Gestisci le richieste ricevute per i tuoi annunci
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pending" className="space-y-6">
        <TabsList className="glass border-white/20 w-full">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            In Attesa ({filteredRequests.pending.length})
          </TabsTrigger>
          <TabsTrigger value="approved" className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            Approvate ({filteredRequests.approved.length})
          </TabsTrigger>
          <TabsTrigger value="rejected" className="flex items-center gap-2">
            <X className="w-4 h-4" />
            Rifiutate ({filteredRequests.rejected.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Tutte ({filteredRequests.all.length})
          </TabsTrigger>
        </TabsList>

        {Object.entries(filteredRequests).map(([status, statusRequests]) => (
          <TabsContent key={status} value={status} className="space-y-4">
            {statusRequests.length === 0 ? (
              <Card className="glass border-white/20">
                <CardContent className="p-12 text-center">
                  <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Nessuna richiesta {status === 'all' ? '' : status === 'pending' ? 'in attesa' : status === 'approved' ? 'approvata' : 'rifiutata'}
                  </h3>
                  <p className="text-muted-foreground">
                    {status === 'pending' 
                      ? 'Le nuove richieste appariranno qui'
                      : 'Non ci sono richieste in questa categoria'
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              statusRequests.map((request) => (
                <Card key={request.id} className="glass border-white/20 hover:border-primary/20 transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-12 h-12 ring-2 ring-primary/20">
                        <AvatarImage src={request.requester_profile.avatar_url || undefined} />
                        <AvatarFallback>
                          {request.requester_profile.username.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-white">
                              {request.requester_profile.display_name || request.requester_profile.username}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              @{request.requester_profile.username}
                            </p>
                          </div>
                          {getStatusBadge(request.status)}
                        </div>

                        <div className="bg-muted/10 p-3 rounded-lg">
                          <p className="text-sm font-medium text-white mb-1">
                            Vuole adottare: {request.adoption_post.pet_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {request.adoption_post.species} • {request.adoption_post.location}
                          </p>
                        </div>

                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {request.message}
                        </p>

                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(request.created_at)}
                          </span>
                          {request.meeting_availability && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Disponibilità: {request.meeting_availability}
                            </span>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setDetailModalOpen(true);
                            }}
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Dettagli
                          </Button>
                          
                          {request.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setReplyModalOpen(true);
                                }}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Check className="w-4 h-4 mr-2" />
                                Approva
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setReplyModalOpen(true);
                                }}
                              >
                                <X className="w-4 h-4 mr-2" />
                                Rifiuta
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Dettagli Richiesta</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={selectedRequest.requester_profile.avatar_url || undefined} />
                  <AvatarFallback>
                    {selectedRequest.requester_profile.username.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {selectedRequest.requester_profile.display_name || selectedRequest.requester_profile.username}
                  </h3>
                  <p className="text-muted-foreground">@{selectedRequest.requester_profile.username}</p>
                  {getStatusBadge(selectedRequest.status)}
                </div>
              </div>

              {selectedRequest.requester_profile.bio && (
                <div>
                  <h4 className="font-medium text-white mb-2">Bio</h4>
                  <p className="text-sm text-muted-foreground">{selectedRequest.requester_profile.bio}</p>
                </div>
              )}

              <div>
                <h4 className="font-medium text-white mb-2">Pet richiesto</h4>
                <div className="bg-muted/10 p-4 rounded-lg">
                  <h5 className="font-medium text-white">{selectedRequest.adoption_post.pet_name}</h5>
                  <p className="text-sm text-muted-foreground">
                    {selectedRequest.adoption_post.species} {selectedRequest.adoption_post.breed && `• ${selectedRequest.adoption_post.breed}`}
                  </p>
                  <p className="text-sm text-muted-foreground">📍 {selectedRequest.adoption_post.location}</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-white mb-2">Messaggio</h4>
                <p className="text-sm text-muted-foreground bg-muted/10 p-4 rounded-lg">
                  {selectedRequest.message}
                </p>
              </div>

              {selectedRequest.meeting_availability && (
                <div>
                  <h4 className="font-medium text-white mb-2">Disponibilità per incontro</h4>
                  <p className="text-sm text-muted-foreground">{selectedRequest.meeting_availability}</p>
                </div>
              )}

              <div className="text-xs text-muted-foreground">
                Ricevuta il {formatDate(selectedRequest.created_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reply Modal */}
      <Dialog open={replyModalOpen} onOpenChange={setReplyModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Rispondi alla Richiesta</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-muted/10 p-4 rounded-lg">
                <p className="text-sm font-medium text-white">
                  {selectedRequest.requester_profile.display_name || selectedRequest.requester_profile.username}
                </p>
                <p className="text-xs text-muted-foreground">
                  Vuole adottare {selectedRequest.adoption_post.pet_name}
                </p>
              </div>

              <div>
                <Label htmlFor="reply">Messaggio di risposta *</Label>
                <Textarea
                  id="reply"
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Scrivi la tua risposta..."
                  rows={4}
                  className="mt-2"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setReplyModalOpen(false)}
                  className="flex-1"
                  disabled={replyLoading}
                >
                  Annulla
                </Button>
                <Button
                  onClick={() => handleReply('approved')}
                  disabled={replyLoading || !replyMessage.trim()}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {replyLoading ? 'Invio...' : 'Approva'}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleReply('rejected')}
                  disabled={replyLoading || !replyMessage.trim()}
                  className="flex-1"
                >
                  <X className="w-4 h-4 mr-2" />
                  {replyLoading ? 'Invio...' : 'Rifiuta'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}